import axios from 'axios';
import { useCallback } from 'react';
import {
  StatsAgreementsResponse,
  StatsByDateResponse,
  StatsCollectionsResponse,
  StatsEventsByDateResponse,
  StatsFiltersResponse,
  StatsJournalsResponse,
  StatsNoResultsResponse,
  StatsQualityResponse,
  StatsTopResponse,
  StatsWidgetOriginsResponse,
} from '../../types/Statistics.types';
import { useApi } from './useApi';

export const useStatistics = () => {
  const { makeApiRequest } = useApi();

  const handleApiRequest = useCallback(
    async <T extends object>(
      url: string,
      method: string = 'GET'
    ): Promise<T> => {
      try {
        return await makeApiRequest<T>(url, method);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const status = error?.response?.status || error?.status || 500;
          if (status >= 500) {
            return [] as T;
          }
        }
        throw error;
      }
    },
    [makeApiRequest]
  );

  const fetchStatisticsTop10 = useCallback(
    async (startDate?: string, endDate?: string) => {
      let url = '/statistics/top';
      if (startDate && endDate) {
        url += `?from=${startDate}&to=${endDate}`;
      }
      return handleApiRequest<StatsTopResponse>(url);
    },
    [handleApiRequest]
  );

  const fetchStatisticsNoResults = useCallback(
    async (startDate?: string, endDate?: string) => {
      let url = '/statistics/no_results';
      if (startDate && endDate) {
        url += `?from=${startDate}&to=${endDate}`;
      }
      return handleApiRequest<StatsNoResultsResponse>(url);
    },
    [handleApiRequest]
  );

  const fetchStatisticsByDate = useCallback(
    async (startDate: string, endDate: string) => {
      return handleApiRequest<StatsByDateResponse>(
        `/statistics/by_date?from=${startDate}&to=${endDate}`
      );
    },
    [handleApiRequest]
  );

  const fetchJournalsByDate = useCallback(
    async (startDate: string, endDate: string) => {
      const journals = await handleApiRequest<StatsJournalsResponse>(
        `/statistics/journals?from=${startDate}&to=${endDate}`
      );
      return journals.sort((a, b) => b.count - a.count);
    },
    [handleApiRequest]
  );

  const fetchFiltersByDate = useCallback(
    async (startDate: string, endDate: string) => {
      const filters = await handleApiRequest<StatsFiltersResponse>(
        `/statistics/filters?from=${startDate}&to=${endDate}`
      );
      return filters.sort((a, b) => b.count - a.count);
    },
    [handleApiRequest]
  );

  const fetchQualityClicks = useCallback(
    async (startDate: string, endDate: string) => {
      return handleApiRequest<StatsQualityResponse>(
        `/statistics/quality?from=${startDate}&to=${endDate}`
      );
    },
    [handleApiRequest]
  );

  const fetchEventsByDate = useCallback(
    async (startDate: string, endDate: string) => {
      const events = await handleApiRequest<StatsEventsByDateResponse>(
        `/statistics/events?from=${startDate}&to=${endDate}`
      );
      return events.sort((a, b) => b.count - a.count);
    },
    [handleApiRequest]
  );

  const fetchAgreementStatistics = useCallback(
    async (startDate: string, endDate: string) => {
      return handleApiRequest<StatsAgreementsResponse>(
        `/statistics/agreements?from=${startDate}&to=${endDate}`
      );
    },
    [handleApiRequest]
  );

  const fetchStatisticsCollections = useCallback(
    async (startDate: string, endDate: string) => {
      return handleApiRequest<StatsCollectionsResponse>(
        `/statistics/collections?from=${startDate}&to=${endDate}`
      );
    },
    [handleApiRequest]
  );

  const fetchWidgetOrigins = useCallback(
    async (startDate: string, endDate: string) => {
      return handleApiRequest<StatsWidgetOriginsResponse>(
        `/statistics/widget_origins?from=${startDate}&to=${endDate}`
      );
    },
    [handleApiRequest]
  );

  return {
    fetchStatisticsTop10,
    fetchStatisticsNoResults,
    fetchStatisticsByDate,
    fetchJournalsByDate,
    fetchFiltersByDate,
    fetchQualityClicks,
    fetchEventsByDate,
    fetchAgreementStatistics,
    fetchStatisticsCollections,
    fetchWidgetOrigins,
  };
};
